<template>
  <aside
    v-if="store.isOpen"
    class="
      tw-flex
      tw-h-full
      tw-w-[533px]
      tw-max-w-full
      tw-shrink-0
      tw-flex-col
      tw-border-y-0
      tw-border-r-0
      tw-border-l-2
      tw-border-solid
      tw-border-l-sf-slate-lighter-1
      tw-bg-white
    "
    data-testid="column-editor-drawer"
    data-walkthrough-id="report-columns-panel"
    role="dialog"
    aria-modal="false"
  >
    <header
      class="
        tw-flex
        tw-items-start
        tw-justify-between
        tw-gap-4
        tw-border-x-0
        tw-border-t-0
        tw-border-b
        tw-border-solid
        tw-border-sf-neutral-lighter-1
        tw-px-5
        tw-py-4
      "
    >
      <div>
        <div class="tw-flex tw-flex-wrap tw-items-center tw-gap-x-3 tw-gap-y-1">
          <h2
            class="
              tw-m-0 tw-text-lg tw-font-semibold tw-text-sf-neutral-darker-1
            "
          >
            {{ t('title') }}
          </h2>
          <SfBannerInline
            v-tooltip.once.placementBottom="t('feedback.tooltip')"
            :variant="bannerVariants.info"
            :aria-label="t('feedback.tooltip')"
            title="Beta"
            single-line
            class="sf-banner--no-border tw-shrink-0"
            role="note"
            tabindex="0"
            data-tooltip-size="sm"
            data-testid="column-editor-beta"
          >
            <template #actions>
              <!-- Appcues launches the feedback flow off this testid. -->
              <SfButtonDs
                variant="secondary"
                size="sm"
                class="tw-text-nowrap"
                data-testid="report-columns-feedback"
              >
                {{ t('feedback.share_button') }}
              </SfButtonDs>
            </template>
          </SfBannerInline>
        </div>
        <p class="tw-mb-0 tw-mt-0.5 tw-text-xs tw-text-sf-neutral-light-2">
          {{ t('subtitle') }}
        </p>
      </div>
      <SfButtonDs
        icon-only
        icon-before="xmark"
        variant="tertiary"
        size="md"
        class="tw-shrink-0"
        :aria-label="t('close')"
        data-testid="column-editor-close"
        @click="store.close()"
      />
    </header>

    <div
      ref="scrollRegion"
      class="tw-flex-1 tw-overflow-y-auto tw-px-5 tw-pb-4 tw-pt-4"
    >
      <p
        v-if="store.status === 'loading'"
        class="tw-text-sm tw-text-sf-neutral-light-2"
        data-testid="column-editor-loading"
      >
        {{ t('loading') }}
      </p>

      <div
        v-else-if="store.status === 'error'"
        class="tw-flex tw-flex-col tw-items-start tw-gap-2"
        data-testid="column-editor-error"
      >
        <p class="tw-m-0 tw-text-sm tw-text-sf-danger">
          {{ t('error') }}
        </p>
        <SfButtonDs
          variant="secondary"
          size="sm"
          data-testid="column-editor-retry"
          @click="store.load()"
        >
          {{ t('retry') }}
        </SfButtonDs>
      </div>

      <!-- `idle` renders nothing: open() loads, so it lasts a tick at most. -->
      <template v-else-if="store.status === 'loaded'">
        <!-- The report is either showing its own columns again or holding a
             preview one edit out of date, and neither is what the list below
             says. Sits with the list rather than with Apply: it is the editing
             that stopped landing. -->
        <p
          v-if="store.previewFailed"
          role="alert"
          class="tw-mb-3 tw-text-sm tw-text-sf-danger"
          data-testid="column-editor-preview-error"
        >
          {{ t('preview_error') }}
        </p>

        <p
          v-if="store.columns.length === 0"
          class="tw-mb-3 tw-text-sm tw-text-sf-neutral-light-2"
          data-testid="column-editor-empty"
        >
          {{ t('empty') }}
        </p>

        <!-- A row's position in this list is the column's position in the
             report, so dragging one is the whole of reordering. -->
        <Draggable
          :model-value="store.columns"
          item-key="uid"
          handle=".handle"
          :animation="200"
          :scroll="false"
          :force-fallback="true"
          class="tw-flex tw-flex-col tw-gap-2"
          @update:model-value="store.reorderColumns"
          @change="onDragChange"
          @start="onDragStart"
          @end="onDragEnd"
        >
          <template #item="{ element: column }: { element: EditorColumn }">
            <ColumnRow
              :column="column"
              :type-schema="store.schema[column.type]"
              @remove="store.removeColumn(column.uid)"
              @expand="store.noteExpanded(column.uid)"
              @update="
                (key, value) => store.updateColumnValue(column.uid, key, value)
              "
            />
          </template>
        </Draggable>

        <!-- The forecast column, when the report has one. Outside the draggable
             list on purpose: the forecast is always the report's last column, so
             there is nothing to drag it past and nothing goes after it. Nor is
             it the drawer's to edit or remove — it belongs to the forecast, and
             "Clear forecast" in the subnav is what takes it away. -->
        <div
          v-if="store.forecastYear !== null"
          class="
            tw-mt-2
            tw-flex
            tw-items-center
            tw-gap-2
            tw-rounded-lg
            tw-border
            tw-border-solid
            tw-border-sf-neutral-lighter-1
            tw-bg-sf-neutral-subtle-1
            tw-px-3
            tw-py-2.5
          "
          data-testid="column-editor-forecast-row"
        >
          <span
            class="
              tw-rounded
              tw-bg-amber-500
              tw-px-1.5
              tw-py-0.5
              tw-text-xs
              tw-uppercase
              tw-text-white
            "
          >
            {{ forecastLabel }}
          </span>
          <span
            class="tw-text-sm tw-font-semibold tw-text-sf-neutral-darker-1"
            data-testid="column-editor-forecast-year"
          >
            {{ store.forecastYear }}
          </span>
          <span class="tw-ml-auto tw-text-xs tw-text-sf-neutral-light-2">
            {{ t('forecast.note') }}
          </span>
        </div>

        <div
          class="
            tw-mt-4
            tw-flex
            tw-flex-wrap
            tw-items-center
            tw-gap-x-4
            tw-gap-y-2
            tw-rounded-lg
            tw-bg-sf-slate-subtle-2
            tw-px-2.5
            tw-py-2
          "
          data-testid="column-editor-add-section"
        >
          <span
            class="
              tw-text-xs
              tw-font-medium
              tw-uppercase
              tw-tracking-wide
              tw-text-sf-neutral-light-2
            "
          >
            {{ t('add.label') }}
          </span>
          <div class="tw-flex tw-flex-wrap tw-gap-2">
            <SfButtonDs
              v-for="type in addableTypes"
              :key="type"
              variant="secondary"
              size="sm"
              :data-testid="`column-editor-add-${type}`"
              @click="store.addColumn(type)"
            >
              {{ t(`add.${addKey[type]}`) }}
            </SfButtonDs>
          </div>
        </div>
      </template>
    </div>

    <footer
      class="
        tw-flex
        tw-items-center
        tw-justify-end
        tw-gap-3
        tw-border-x-0
        tw-border-b-0
        tw-border-t
        tw-border-solid
        tw-border-sf-neutral-lighter-1
        tw-px-5
        tw-py-3
      "
    >
      <!-- Puts the report's own columns back in the list. Off while there is
           nothing to undo, and while a render is on its way for the same reason
           Apply is: the columns that render belong to the request in flight.
           The report table is not touched. It takes another Apply to put those
           columns on the page. -->
      <button
        type="button"
        :class="footerLinkClasses"
        :disabled="!store.hasChanges || store.applying"
        data-testid="column-editor-clear"
        data-walkthrough-id="report-columns-reset"
        @click="store.clearChanges()"
      >
        <i class="fa fa-rotate-left" />
        {{ t('footer.clear') }}
      </button>

      <!-- Period sets are not built yet. The control is in the footer so the
           design holds together, and the tooltip is the only thing that tells
           the user why it will not respond. `aria-disabled` rather than
           `disabled`: the tooltip is the whole explanation, and a disabled
           button cannot be focused, so a keyboard user would never reach it. -->
      <button
        v-tooltip.once="t('footer.save_period_set_tooltip')"
        type="button"
        aria-disabled="true"
        :class="[
          footerLinkClasses,
          'tw-mr-auto tw-cursor-not-allowed tw-text-sf-neutral-light-2',
        ]"
        data-testid="column-editor-save-period-set"
        @click.prevent
      >
        <i class="fa fa-floppy-disk" />
        {{ t('footer.save_period_set') }}
      </button>

      <p
        v-if="store.applyFailed"
        role="alert"
        class="tw-m-0 tw-text-sm tw-text-sf-danger"
        data-testid="column-editor-apply-error"
      >
        {{
          store.applyError === 'too_large'
            ? t('footer.apply_error_too_large')
            : t('footer.apply_error')
        }}
      </p>

      <!-- Nothing to apply until the columns are loaded; `loading` takes the
           button out of reach on its own while the render is on its way. A
           report with no columns is nothing but its row labels, so an empty
           list is not something to apply either — and neither is a list the
           table on screen is already rendered for. -->
      <SfButtonDs
        variant="primary"
        class="tw-shrink-0"
        :disabled="
          store.status !== 'loaded' ||
          store.columns.length === 0 ||
          !store.hasUnappliedChanges
        "
        :loading="store.applying"
        data-testid="column-editor-apply"
        @click="store.requestApply()"
      >
        {{ t('footer.apply') }}
      </SfButtonDs>
    </footer>
  </aside>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import Draggable from 'vuedraggable'

import useDragAutoscroll from '@app/shared/vue/composables/use-drag-autoscroll'
import useI18n from '@app/shared/vue/composables/use-I18n'
import { bannerVariants } from '@app/shared/types/banner'
import SfBannerInline from '@app/shared/vue/components/sf-banner/sf-banner-inline.vue'
import { SfButtonDs } from '@silverfin/ui-components'

import type { DragLikeEvent } from '@app/shared/vue/composables/use-drag-autoscroll'
import type { DraggableChange, EditorColumn } from '../types'
import type { AddableType } from '../use-column-editor'

import { ADDABLE_TYPES, useColumnEditor } from '../use-column-editor'
import { useColumnEditorI18n } from '../use-column-editor-i18n'
import ColumnRow from './column-row.vue'

const store = useColumnEditor()

// The "+" buttons this report gets: the types the server says it may take, in the
// drawer's own order rather than the order the response happens to list them in.
const addableTypes = computed(() =>
  ADDABLE_TYPES.filter((type) => store.addable.includes(type))
)

const scrollRegion = ref<HTMLElement | null>(null)

// Scrolls the list while a dragged row dwells near its edge. Sortable can do
// this itself, but only while the cursor stays inside the scrolling element —
// and the footer sits right below it, so overshooting a downward drag by a few
// pixels killed the scroll exactly when it was needed. Driving it off the
// pointer instead keeps it going past the edge.
//
// The boundaries are the list's own visible edges, not the viewport's: the page
// doesn't scroll here, the list does. `force-fallback` above is what makes this
// work at all — native HTML5 drag-and-drop suppresses pointer events, which is
// all this composable has to go on — and `scroll: false` stops Sortable from
// scrolling alongside it.
// Measured once per drag rather than per frame: the composable asks for both
// edges every rAF tick, and a layout read between its scroll writes would force
// a reflow each frame. Nothing moves the region while a row is in flight.
const bounds = { top: 0, bottom: 0 }

const { onDragStart: startAutoscroll, onDragEnd } = useDragAutoscroll({
  topBoundary: () => bounds.top,
  bottomBoundary: () => bounds.bottom,
})

// No fallback for a missing region: a row can only be dragged out of one that is
// on screen, so a null here is a bug worth hearing about rather than papering
// over with viewport edges that would scroll in the wrong zone.
function onDragStart(event: DragLikeEvent): void {
  const region = scrollRegion.value!

  // A list that fits has nothing to scroll, and the composable picks its target
  // by climbing to the nearest ancestor that is *currently* overflowing — so it
  // would skip the region and drive the page instead, against the region's edges.
  if (region.scrollHeight <= region.clientHeight) return

  const { top, bottom } = region.getBoundingClientRect()

  bounds.top = top
  bounds.bottom = bottom

  startAutoscroll(event)
}

// Which row was dragged, which only the drag knows: an adjacent swap makes the
// same two lists either way round. Silent for a row dropped where it started.
function onDragChange(event: DraggableChange): void {
  if (!event.moved) return

  store.noteReorder(event.moved)
}

const t = useColumnEditorI18n()

// The word the forecast column's own header carries, so the row in the list and
// the column in the report read the same.
const forecastLabel = useI18n().t('reports.forecast.column_header_label')

// Maps an addable column type to its "+" button i18n key.
// Compact text links rather than an sf-button variant: every variant is too
// loud for the left of the footer.
const footerLinkClasses =
  'tw-flex tw-appearance-none tw-items-center tw-gap-1.5 tw-border-0 tw-bg-transparent tw-text-xs tw-font-normal tw-text-sf-neutral-dark-1 hover:tw-text-sf-neutral-darker-1 disabled:tw-cursor-not-allowed disabled:tw-text-sf-neutral-light-2'

const addKey: Record<AddableType, string> = {
  ColumnSpacer: 'empty_space',
  Period: 'period',
  PeriodDifference: 'difference',
  PeriodAnalytical: 'analytical',
}
</script>
